import {
  createDefaultNativeStackNavigator,
  createModalScreenOptions,
  createWebModalScreenOptions,
} from 'app/lib/navigation';
import { NeighbourListingsScreen } from 'app/screens/billboard/neighbour-listings';
import { NeighbourProfileScreen } from 'app/screens/billboard/neighbour-profile';
import { OneChannelScreen } from 'app/screens/chat/one-channel';
import { GroupMembersScreen } from 'app/screens/groups/group-members-screen';
import { LoanDetailsScreen } from 'app/screens/management/one-loan';
import type { Address } from 'app/types/address';
import type { Channel, TextMessageMessage } from 'app/types/chat';
import type { Listing, ListingType, TransactionMode } from 'app/types/listing';
import type { Loan, LoanType } from 'app/types/loan';
import type { FindAllUsersDTO, User } from 'app/types/user';
import { LoanDatesPickerScreen } from '../shared/loan-dates-picker';
import { OneListingScreen } from '../shared/one-listing';
import { RatingsScreen } from '../shared/ratings';

import { FavouritesScreen } from './favourites';
import { LoanParticipantsScreen } from './loan-participants';
import { ManagementOverviewScreen } from './management-overview';
import { MyListingsScreen } from './my-listings';
import { MyLoansScreen } from './my-loans';
import { PastLoansScreen } from './past-loans';
import { LenderParticipateScreen } from '../shared/one-listing/listing-activity-screen';
import { useUISize } from 'app/utils/web-helpers';
import type { ComponentProps } from 'react';

export type Parameters = {
  'management-overview': undefined;
  'my-address': {
    id?: Address['id'];
    address?: Listing['address'] | null;
    goBackTo?: 'new' | 'one-listing' | 'edit-listing';
  };
  'my-loans': { type?: LoanType };
  'my-listings': { type: ListingType; isSelecting?: boolean };
  'neighbour-profile': {
    userId: User['id'];
    listingId?: Listing['id'];
    listingType?: Listing['type'];
    channelURL?: Channel['channel_url'];
  };
  'neighbour-listings': { type: Listing['type']; userId: User['id'] };
  'one-channel': { url: string; messagePrompt?: TextMessageMessage; toast?: string };
  'one-listing': {
    id: Listing['id'];
    newServiceCategoryId?: string;
    newCategoryId?: string;
    newAddress?: Listing['address'];
  };
  'listing-activity': {
    listingId: Listing['id'];
    isLended: boolean;
  };
  ratings: {
    userId?: User['id'];
    listingId?: Listing['id'];
    subjectName?: string;
    listingTitle?: string;
    scrollToLoanId?: Loan['id'];
  };
  'one-loan': { id: Loan['id']; listingId?: Listing['id'] };
  'loan-participants': { loanId: Loan['id'] };
  'past-loans': { type?: LoanType; ratingFilter?: 'unrated'; transactionMode?: TransactionMode } | undefined;
  favourites: undefined;
  'group-members': FindAllUsersDTO;
  'loan-dates-picker': {
    url: string;
    promptIntroduceYourself: boolean;
    goBackTo: 'one-channel' | 'one-loan';
    loanId?: Loan['id'];
  };
};

export { linking } from './navigator.linking';

const Stack = createDefaultNativeStackNavigator<Parameters>();

export const initialRouteName = 'management-overview';

export function Navigator() {
  return (
    <Stack.Navigator initialRouteName={initialRouteName} screenOptions={{ cardStyle: { backgroundColor: 'white' } }}>
      <Stack.Screen component={ManagementOverviewScreen} name="management-overview" />
      <Stack.Screen component={MyLoansScreen} name="my-loans" />
      <Stack.Screen component={MyListingsScreen} getId={({ params }) => params.type} name="my-listings" />
      <Stack.Screen component={FavouritesScreen} name="favourites" />
      <Stack.Screen component={PastLoansScreen} name="past-loans" />
    </Stack.Navigator>
  );
}

const Stack2 = createDefaultNativeStackNavigator<Parameters>({
  withChat: false,
});

export function NavigatorLoan() {
  const { isSmallUI } = useUISize();
  return (
    <Stack2.Navigator
      initialRouteName="one-loan"
      screenOptions={{
        cardStyle: { backgroundColor: 'white' },
      }}
    >
      <Stack2.Screen
        component={LoanDetailsScreen}
        name="one-loan"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack2.Navigator>;
          }

          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack2.Screen component={LoanParticipantsScreen} name="loan-participants" />
      <Stack2.Screen component={OneChannelScreen} name="one-channel" />
      <Stack2.Screen
        component={OneListingScreen}
        name="one-listing"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack2.Navigator>;
          }

          return {};
        }}
      />
      <Stack2.Screen
        component={LenderParticipateScreen}
        name="listing-activity"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack2.Navigator>;
          }

          return {};
        }}
      />
      <Stack2.Screen
        component={RatingsScreen}
        name="ratings"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack2.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack2.Screen
        component={NeighbourProfileScreen}
        name="neighbour-profile"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack2.Navigator>;
          }
          return {};
        }}
      />
      <Stack2.Screen
        component={NeighbourListingsScreen}
        name="neighbour-listings"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack2.Navigator>;
          }
          return {};
        }}
      />
      <Stack2.Screen component={GroupMembersScreen} name="group-members" />
      <Stack2.Screen
        component={LoanDatesPickerScreen}
        name="loan-dates-picker"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack2.Navigator>;
          }
          return {};
        }}
      />
    </Stack2.Navigator>
  );
}
